/* eslint-disable no-unused-vars */
import { store } from '../../store';
import { FreightCostTypes } from '../../../../shared/types/freightCost/freightCostType';
import { ExcelImportValidation } from '../../../../shared/utils/enums';
import { Order } from './order';
import { FilterExternalIdsEntity, FilterType } from '../filter';
import { Freight, Pickup } from './index';

export type FilterEntityTypes =
  | 'pickup'
  | 'freight'
  | 'order'
  | 'freightBill'
  | 'operativeStatistics'
  | 'costReportStatistics'
  | 'capacityStatistics'
  | 'financialStatistics';
export enum DateFilterTypes {
  today = 'today',
  this_week = 'this_week',
  this_month = 'this_month',
  this_year = 'this_year'
}
export type DefaultFilters = Record<FilterEntityTypes, Record<string, any>>;

export type ExcelConfigHeader = {
  excelHeader: string;
  logisticsField: string;
  validations?: ExcelImportValidation[];
};

export type MapPlannerOptionsField = {
  /*
    mdi icon to show in the header,
   if provided, label will be used as tooltip
  */
  icon?: string;
  label?: string; // label to show in the component
  field: string; // the field name in the feature properties
  link?: boolean; // whether the field value should be shown as a link
  valuePrefix?: string; // prefix to add before the field value
  valueSuffix?: string; // suffix to add after the field value
  cssStyle?: string; // custom CSS style for the field value
  cols?: number; // number of columns the field should span in the component, value between 1-12
  width?: number; // width of the field in the component, e.g. '100'
  summary?: boolean; // whether to show a summary section in the order summary panel, default: false
};

export type PopupOptions = {
  title?: string; // the title of the popup
  maxWidth?: string; // the maxWidth of the popup, e.g. '400px'
  fields: Array<MapPlannerOptionsField>; // fields to show in the popup
};

export type SummaryOrderOptions = {
  fields: Array<MapPlannerOptionsField>; // fields to show in the summary panel for orders
};

export type SummaryFreightOptions = {
  routeColors: {
    prePlanned: string; // color for showing preplanned freight route on map
    planned: string; // color for showing actually planned freight route on map
  };
  fields: Array<MapPlannerOptionsField>; // fields to show in the summary panel for freights
};

export type SummaryOptions = {
  // the title of the summary panel
  title?: string;
  // the maxWidth of the summary panel, e.g. '400px'
  maxWidth?: string;
  // the minWidth of the summary panel, e.g. '300px'
  minWidth?: string;
  // options for order summary
  order: SummaryOrderOptions;
  // options for freight summary
  freight: SummaryFreightOptions;
  // whether to consider (by default) time and distance when (pre)planning
  planByDefaultConsideringTimeAndDistance?: boolean;
};

export type MapPlannerOptions = {
  mapColors?: {
    // colors for order statuses that can be used in freight planning on the map planner
    selectable: Record<string, string>;
    // colors for order statuses that cannot be used in freight planning on the map planner
    notSelectable: Record<string, string>;
  };
  popup?: PopupOptions; // popup options for the map planner,
  summary?: SummaryOptions; // summary panel options for the map planner
  filter?: boolean; // defines if the map planner has a filter panel
};

export type ColumnSize = {
  cols?: number; // default: 4 (1-12)
  md?: number; // default: cols
  xl?: number; // default: Math.ceil(md / 2)
};

export type ShowIfConfig = {
  check: 'user_role' | 'ability';
  value: string[];
  negate?: boolean; // if true, the condition will be negated
};

export type HeaderConfig = {
  position: number; // the order of the column in the table
  text?: string; // if missing, the text from OrderEntityConfig will be used
  sortable?: boolean; // default: true
  width?: number | string; // e.g. '150px' or 100
  align?: 'left' | 'center' | 'right'; // default: 'left'
  hideFromRoles?: string[]; // array of user roles from which this column should be hidden
  showOnlyForRoles?: string[]; // array of user roles only for which this column should be shown
};

export type FilterConfig = ColumnSize & {
  type: FilterType;
  position: number; // the order of the filter in the filter panel
  // the field name to filter by, if missing, the value from BaseEntityConfig will be used
  value?: string;
  label?: string; // if missing, the text from OrderEntityConfig will be used
  placeholder?: string;
  filterForEmptyValues?: boolean; // default: false
  emptyValue?: unknown;
  /*
  For multi-select and multi-autocomplete type filters, how many selected items is shown
  before the rest are hidden under "+X" label. Default: all items are shown.
  */
  maxItemsToShow?: number;
  /*
  As a filter object can contain external ids for multiple entities,
  this property defines under which key should the external ids
  be stored to make the filter work properly.
  Mandatory only for external id based filters.
  */
  entityType?: FilterExternalIdsEntity;
  /*
  Used only if customizable filter component is split into main and extended filters.
  Filters marked with isMain: true will be shown in the main filter panel,
  others will be in the extended filters section.
  */
  isMain?: boolean;
  // position within the split filter sections, fallback to 'position' if missing
  splitPosition?: number;
  // size within the split filter sections, fallback to unsplit ColumnSize if missing
  splitSize?: ColumnSize;
  stepValue?: number; // step value for number range filters
  showIf?: ShowIfConfig; // condition for showing the filter based on user role or ability
};

export type ExcelConfig = {
  position: number; // the order of the column in the excel export
  dateFormat?: string; // if the field is a date, the string format to use in excel export
  text?: string; // if missing, the text from OrderEntityConfig will be used
  value?: string; // the field name in the data to export
  noDataText?: string; // default ''
  /*
    Whether the field is a price, if true, it will be formatted
    with getSegmentedPrice function in excel export
  */
  isPrice?: boolean;
  /*
    The value of the field will be multiplied by this number and then rounded
    with Math.round() in excel export.
    Without this property or with zero value, the original value will be exported.
  */
  multiplyThenRound?: number;
  // if the field value is an array, it will be joined with this string in excel export
  joinWith?: string;
  // if the field value is an array, whether to use only distinct values in excel export
  distinctValues?: boolean;
  /*
    If true, getPalletCountAndWeight function will be used
    to calculate the value based on orderItems
  */
  isCalculatedPalletsCount?: boolean;
  hideFromRoles?: string[]; // array of user roles from which this column should be hidden
  showOnlyForRoles?: string[]; // array of user roles only for which this column should be shown
};

type BaseEntityConfig = {
  text: string;
  header?: HeaderConfig; // if missing, it won't appear in the table header
  filter?: FilterConfig; // if missing, it won't appear in the filter options
  excel?: ExcelConfig; // if missing, it won't appear in the excel export header
};

export type OrderEntityConfig = BaseEntityConfig & {
  value: keyof Order;
};

export type FreightEntityConfig = BaseEntityConfig & {
  value: keyof Freight;
};

export type PickupEntityConfig = BaseEntityConfig & {
  value: keyof Pickup;
};

export type EntitiesConfig = {
  orders: OrderEntityConfig[];
  freights: FreightEntityConfig[];
  pickups: PickupEntityConfig[];
};

export type Config = {
  view: {
    id: string;
    title: string;
    mode: string;
    favicon: string;
    logo: string;
    largeLogo: string;
    logoWidth: string;
    theme: {
      brand?: string;
      'on-brand'?: string;
      primary: string;
      secondary: string;
      accent: string;
      error: string;
      info: string;
      success: string;
      warning: string;
      background: string;
      sidepanel: string;
      tollRoadLayer: string;
      weightLimitZoneLayer: string;
      buttonText: string;
      selectionText: string;
    };
  };
  entities: EntitiesConfig;
  mainVehicle: string;
  mapPlannerOptions?: MapPlannerOptions;
  defaultFilters: DefaultFilters;
  colors: string[];
  disableAuth: boolean;
  testOrdersJson?: any;
  units: {
    [key: string]: {
      extendedNames: string[];
      displayName: string;
      unitForPDF: string;
      columnHeaderName: string;
      isOnlyInteger: boolean;
    };
  };
  productCategory: string[];
  freightCostTypes: FreightCostTypes;
  palletTypes: Record<string, { kg: number; dimensions: [number, number] }>;
  partialPalletRoundingLimit: number;
  xlsxHeaders?: Record<
    string,
    { requiredHeaders?: ExcelConfigHeader[]; optionalHeaders?: ExcelConfigHeader[] }
  >;
  logisticsPlannerType: typeof store.state.app.enums.LogisticsPlannerType;
  fieldEditSettings: Record<string, any>;
};
